import { creatorbase, creatorbaseQueries } from '@round/api';
import { GenericDropdownOption } from 'App.types';
import useSelectProps from 'Hooks/useSelectProps';
import { useInfiniteQuery } from '@tanstack/react-query';
import { SelectProps } from '@round/ui-kit';
import { useTeam } from '@round/creatorbase-helpers/api-hooks';
import { isNumber } from 'utility/utility';
import { useMemo } from 'react';

const queryOptions = creatorbaseQueries.team.options.infinite;

export type TeamOption = GenericDropdownOption<number>;

export const mapTeamToOption = (team: creatorbase.Team): TeamOption => ({
    value: team.id,
    label: team.name,
});

type Params = {
    initOn: 'menuOpen' | 'mount';
    initialValueId?: number;
    params?: Pick<creatorbase.GetTeamsParams, 'id' | 'team_only' | 'user_with_direct_access_id' | 'client_id'>;
    enabled?: boolean;
};

type Return = {
    getProps: (override?: Pick<SelectProps<TeamOption>, 'onChange'>) => SelectProps<TeamOption>;
};

export default function useTeamSelect({ enabled = true, ...params }: Params = { initOn: 'mount' }): Return {
    const { data: team } = useTeam(params.initialValueId, {}, { enabled: isNumber(params.initialValueId) });
    const initialValue = useMemo(() => (team ? mapTeamToOption(team) : null), [team]);

    const { getProps, isMenuOpen, debouncedSearchValue } = useSelectProps<TeamOption, false>({ initialValue });

    const { data, isFetching, hasNextPage, fetchNextPage, isError } = useInfiniteQuery({
        ...queryOptions({ ...params.params, page_size: 25, search: debouncedSearchValue }),
        enabled: enabled && ((params.initOn === 'menuOpen' && isMenuOpen) || params.initOn === 'mount'),
    });

    const options =
        data?.pages.flatMap((page) => {
            return page.results.map(mapTeamToOption);
        }) ?? [];

    return {
        getProps: (override) =>
            getProps({
                ...override,
                options,
                noOptionsMessage: () => (isError ? 'Could not get teams' : 'No teams found'),
                isLoading: isFetching,
                onMenuScrollToBottom: () => {
                    if (hasNextPage && !isFetching) {
                        fetchNextPage();
                    }
                },
                filterOption: null,
            }),
    };
}
